<script setup>
import { computed, onMounted, reactive, ref, watch } from 'vue';
import { useI18n } from 'vue-i18n';
import { useAlert } from 'dashboard/composables';
import CaptainCredentialAPI from 'dashboard/api/captain/credential';

import Button from 'dashboard/components-next/button/Button.vue';
import Icon from 'dashboard/components-next/icon/Icon.vue';
import Input from 'dashboard/components-next/input/Input.vue';
import Switch from 'dashboard/components-next/switch/Switch.vue';
import Select from 'dashboard/components-next/select/Select.vue';
import { PROVEDORES } from 'dashboard/routes/dashboard/agenteIa/chaves';

const emit = defineEmits(['saved']);

const { locale, t } = useI18n();

// Provedores que o Copiloto entende (Captain::AccountCredential::PROVIDERS); o tipo da chave e o proprio id.
const COPILOT_PROVIDERS = ['openai', 'openrouter', 'anthropic', 'gemini'];
const providerOptions = PROVEDORES.filter(item =>
  COPILOT_PROVIDERS.includes(item.kind)
);

const emptyCredential = () => ({
  configured: false,
  enabled: false,
  provider: 'openai',
  masked_api_key: '',
  last_verified_at: null,
});

const credential = ref(emptyCredential());
const apiKey = ref('');
const form = reactive({
  enabled: false,
  provider: 'openai',
});
const isFetching = ref(true);
const isSaving = ref(false);
const hasFetchError = ref(false);

const hasSavedKey = computed(() => Boolean(credential.value.masked_api_key));

const status = computed(() => {
  if (hasSavedKey.value && form.enabled) {
    return {
      label: t('CAPTAIN_SETTINGS.CREDENTIAL.STATUS.ACTIVE'),
      className: 'bg-n-teal-3 text-n-teal-11',
      dotClassName: 'bg-n-teal-9',
    };
  }

  if (hasSavedKey.value) {
    return {
      label: t('CAPTAIN_SETTINGS.CREDENTIAL.STATUS.DISABLED'),
      className: 'bg-n-amber-3 text-n-amber-11',
      dotClassName: 'bg-n-amber-9',
    };
  }

  return {
    label: t('CAPTAIN_SETTINGS.CREDENTIAL.STATUS.NOT_CONFIGURED'),
    className: 'bg-n-slate-3 text-n-slate-11',
    dotClassName: 'bg-n-slate-9',
  };
});

const providerChanged = computed(
  () => hasSavedKey.value && form.provider !== credential.value.provider
);

const isDirty = computed(() => {
  return (
    Boolean(apiKey.value.trim()) ||
    form.enabled !== Boolean(credential.value.enabled)
  );
});

// Trocar de provedor pede a chave nova: a salva e do provedor antigo.
const canSave = computed(() => {
  const hasKey =
    Boolean(apiKey.value.trim()) ||
    (hasSavedKey.value && !providerChanged.value);
  return hasKey && isDirty.value && !isFetching.value && !isSaving.value;
});

const apiKeyHelp = computed(() => {
  if (providerChanged.value) {
    return t('CAPTAIN_SETTINGS.CREDENTIAL.API_KEY.NEW_PROVIDER_HELP');
  }
  return hasSavedKey.value
    ? t('CAPTAIN_SETTINGS.CREDENTIAL.API_KEY.REPLACE_HELP')
    : t('CAPTAIN_SETTINGS.CREDENTIAL.API_KEY.HELP');
});

const providerLink = computed(
  () => providerOptions.find(item => item.id === form.provider)?.link
);

const providerSelectOptions = computed(() =>
  providerOptions.map(item => ({
    value: item.id,
    label: t(`CAPTAIN_SETTINGS.CREDENTIAL.PROVIDERS.${item.id}.LABEL`),
  }))
);

const formattedVerificationDate = computed(() => {
  if (!credential.value.last_verified_at) return '';

  const date = new Date(credential.value.last_verified_at);
  if (Number.isNaN(date.getTime())) return '';

  return new Intl.DateTimeFormat(locale.value.replace('_', '-'), {
    dateStyle: 'medium',
    timeStyle: 'short',
  }).format(date);
});

const populateForm = data => {
  credential.value = { ...emptyCredential(), ...data };
  form.enabled = Boolean(data.enabled);
  form.provider = data.provider;
  apiKey.value = '';
};

const fetchCredential = async () => {
  isFetching.value = true;
  hasFetchError.value = false;

  try {
    const { data } = await CaptainCredentialAPI.get();
    populateForm(data);
  } catch (error) {
    hasFetchError.value = true;
  } finally {
    isFetching.value = false;
  }
};

const saveCredential = async () => {
  if (!hasSavedKey.value && !apiKey.value.trim()) {
    useAlert(t('CAPTAIN_SETTINGS.CREDENTIAL.API.KEY_REQUIRED'));
    return;
  }

  isSaving.value = true;

  const payload = {
    enabled: form.enabled,
    provider: form.provider,
  };

  if (apiKey.value.trim()) {
    payload.api_key = apiKey.value.trim();
  }

  try {
    const { data } = await CaptainCredentialAPI.update(payload);
    populateForm(data);
    emit('saved');
    useAlert(t('CAPTAIN_SETTINGS.CREDENTIAL.API.SUCCESS'));
  } catch (error) {
    const message = error.response?.data?.error;
    useAlert(message || t('CAPTAIN_SETTINGS.CREDENTIAL.API.ERROR'));
  } finally {
    isSaving.value = false;
  }
};

watch(apiKey, (value, previousValue) => {
  if (value.trim() && !previousValue.trim() && !hasSavedKey.value) {
    form.enabled = true;
  }
});

onMounted(fetchCredential);
</script>

<template>
  <div
    v-if="isFetching"
    class="flex min-h-32 items-center justify-center rounded-xl border border-n-weak bg-n-alpha-1"
  >
    <woot-loading-state :message="t('CAPTAIN_SETTINGS.CREDENTIAL.LOADING')" />
  </div>

  <div
    v-else-if="hasFetchError"
    class="flex flex-col items-start gap-3 rounded-xl border border-n-weak bg-n-alpha-1 p-5"
  >
    <p class="m-0 text-sm text-n-slate-11">
      {{ t('CAPTAIN_SETTINGS.CREDENTIAL.API.FETCH_ERROR') }}
    </p>
    <Button
      :label="t('CAPTAIN_SETTINGS.CREDENTIAL.RETRY')"
      variant="outline"
      color="slate"
      size="sm"
      @click="fetchCredential"
    />
  </div>

  <form v-else class="grid gap-5" @submit.prevent="saveCredential">
    <div
      class="flex flex-col gap-4 rounded-xl border border-n-weak bg-n-alpha-1 p-5 sm:flex-row sm:items-center sm:justify-between"
    >
      <div class="flex flex-col gap-2">
        <div class="flex flex-wrap items-center gap-2">
          <span class="text-sm font-medium text-n-slate-12">
            {{ t('CAPTAIN_SETTINGS.CREDENTIAL.PROVIDER') }}
            {{
              t(
                `CAPTAIN_SETTINGS.CREDENTIAL.PROVIDERS.${credential.provider}.LABEL`
              )
            }}
          </span>
          <span
            class="inline-flex w-fit items-center gap-1.5 rounded-full px-2.5 py-1 text-xs font-medium"
            :class="status.className"
          >
            <span class="size-1.5 rounded-full" :class="status.dotClassName" />
            {{ status.label }}
          </span>
        </div>
        <p v-if="hasSavedKey" class="m-0 text-sm text-n-slate-11">
          {{ t('CAPTAIN_SETTINGS.CREDENTIAL.SAVED_KEY') }}
          <span class="font-mono text-n-slate-12">
            {{ credential.masked_api_key }}
          </span>
        </p>
        <p v-if="formattedVerificationDate" class="m-0 text-xs text-n-slate-10">
          {{
            t('CAPTAIN_SETTINGS.CREDENTIAL.LAST_VERIFIED', {
              date: formattedVerificationDate,
            })
          }}
        </p>
      </div>

      <div class="flex items-center gap-3 text-sm text-n-slate-12">
        {{ t('CAPTAIN_SETTINGS.CREDENTIAL.ENABLED') }}
        <Switch v-model="form.enabled" :disabled="isSaving" />
      </div>
    </div>

    <div class="grid gap-4">
      <div class="grid gap-1.5">
        <label class="text-sm font-medium text-n-slate-12">
          {{ t('CAPTAIN_SETTINGS.CREDENTIAL.PROVIDER_FIELD') }}
        </label>
        <Select
          v-model="form.provider"
          :options="providerSelectOptions"
          :disabled="isSaving"
          :aria-label="t('CAPTAIN_SETTINGS.CREDENTIAL.PROVIDER_FIELD')"
        />
        <p class="m-0 text-xs text-n-slate-10">
          {{ t(`CAPTAIN_SETTINGS.CREDENTIAL.PROVIDERS.${form.provider}.HINT`) }}
          <a
            :href="providerLink"
            target="_blank"
            rel="noopener noreferrer"
            class="font-medium text-n-blue-11 hover:underline"
          >
            {{ t('CAPTAIN_SETTINGS.CREDENTIAL.CREATE_KEY') }}
          </a>
        </p>
      </div>
      <div class="grid gap-1.5">
        <Input
          v-model="apiKey"
          type="password"
          autocomplete="new-password"
          :disabled="isSaving"
          :label="t('CAPTAIN_SETTINGS.CREDENTIAL.API_KEY.LABEL')"
          :placeholder="
            hasSavedKey && !providerChanged
              ? credential.masked_api_key
              : t('CAPTAIN_SETTINGS.CREDENTIAL.API_KEY.PLACEHOLDER')
          "
        />
        <p class="m-0 text-xs text-n-slate-10">
          {{ apiKeyHelp }}
        </p>
      </div>
    </div>

    <div
      class="flex gap-3 rounded-xl border border-n-amber-7 bg-n-amber-2 p-4 text-sm text-n-amber-12"
    >
      <Icon
        icon="i-lucide-credit-card"
        class="mt-0.5 size-4 shrink-0"
        aria-hidden="true"
      />
      <p class="m-0">
        {{ t('CAPTAIN_SETTINGS.CREDENTIAL.COST_NOTICE') }}
      </p>
    </div>

    <div class="flex justify-end">
      <Button
        type="submit"
        :label="t('CAPTAIN_SETTINGS.CREDENTIAL.SAVE')"
        :is-loading="isSaving"
        :disabled="!canSave"
      />
    </div>
  </form>
</template>
